import React, {useState, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {formatPrice, priceDecimals} from '../../helpers/PriceHelper';
import {getDeadlineDate, formatDeadlineShort} from '../fields/Urgency';
import AnimatedNumber from '../../../common/AnimatedNumber';

export default function FinalStepModal({options, calculator, values, onConfirm, onSkip, onClose}) {
    const isOpen = useSelector(state => state.modal.finalStep);
    const {services, features, totalPrice, subtotal, discount, coupon} = calculator;
    const couponApplied = !!(coupon && coupon.data && coupon.data.isValid && discount > 0);

    // Upsell selection — opt-in. Modal opens with nothing pre-checked
    // so the customer is never charged for upsell items they didn't
    // explicitly tick. Reset on each open so a previously-clicked-then-
    // skipped session doesn't leak state into the next attempt.
    const [modalServices, setModalServices] = useState([]);
    const [modalFeatures, setModalFeatures] = useState([]);

    useEffect(() => {
        if (isOpen) {
            setModalServices([]);
            setModalFeatures([]);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    // Two themed hero-arts. `pickArt(name)` matches by keyword in the
    // feature's display name — letter-shaped fallback covers the long
    // tail of "letter / cover / follow-up / thank-you" upsells.
    const heroVariants = {
        profile: (
            <span className="modal-final-step__option-art modal-final-step__option-art--profile" aria-hidden="true">
                <svg viewBox="0 0 32 32" width="32" height="32">
                    <rect x="2" y="6" width="28" height="20" rx="3" fill="#fff" stroke="#2f8f57" strokeWidth="1.5" />
                    <circle cx="9" cy="14" r="3.5" fill="#3fb874" className="ms-art-pulse" />
                    <path d="M5 22c.5-2 2.2-3.2 4-3.2s3.5 1.2 4 3.2" stroke="#3fb874" strokeWidth="1.4" fill="none" strokeLinecap="round" />
                    <rect x="15" y="11" width="11" height="2" rx="1" fill="#2f8f57" />
                    <rect x="15" y="15" width="8" height="2" rx="1" fill="#9bc2a6" />
                    <rect x="15" y="19" width="10" height="1.6" rx="0.8" fill="#cce4d6" />
                </svg>
            </span>
        ),
        envelope: (
            <span className="modal-final-step__option-art modal-final-step__option-art--envelope" aria-hidden="true">
                <svg viewBox="0 0 32 32" width="32" height="32">
                    <rect x="3" y="9" width="26" height="17" rx="2" fill="#fff" stroke="#2f8f57" strokeWidth="1.5" />
                    <path d="M3 10l13 9 13-9" fill="none" stroke="#2f8f57" strokeWidth="1.5" strokeLinejoin="round" />
                    <g className="ms-art-seal">
                        <circle cx="25" cy="9" r="4.2" fill="#f5a623" stroke="#fff" strokeWidth="1.5" />
                        <path d="M22.8 9.2l1.6 1.6 3-3" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                </svg>
            </span>
        ),
    };

    const pickArt = (name) => {
        const n = (name || '').toLowerCase();
        if (n.includes('linkedin') || n.includes('profile')) return heroVariants.profile;
        return heroVariants.envelope;
    };

    const toggleService = (key) => {
        setModalServices(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    };

    const toggleFeature = (key) => {
        setModalFeatures(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    };

    // Sum upsell prices on top of the *already discounted* totalPrice.
    // Discount only applies to the customer's pre-modal selection — any
    // upsells they tick now go in at full price.
    let newTotal = totalPrice;
    modalServices.forEach(key => {
        if (services[key]) newTotal += parseFloat(priceDecimals(services[key].price));
    });
    modalFeatures.forEach(key => {
        if (features[key]) newTotal += parseFloat(priceDecimals(features[key].price));
    });

    const careerName = options.careerLevel[values.careerLevel]?.name || '';
    const deadline = getDeadlineDate(values.urgency, options.urgency);

    const selectedServices = values.services || [];
    const selectedFeatures = values.features || [];

    return <div className="modal fade in" style={{display: 'block'}}>
        <div className="modal-dialog modal-final-step">
            <div className="modal-content">
                <div className="modal-header">
                    <p className="text-left text-center">It's almost done</p>
                </div>
                <div className="modal-body">
                    <div className="modal-final-step__body">
                        <div className="modal-final-step__layout">
                                <div className="modal-final-step__order-list">
                                    <div className="modal-final-step__title">Order List</div>
                                    <div className="modal-final-step__level">
                                        <div className="modal-final-step__summary-row">
                                            <div className="modal-final-step__summary-label text-left"><strong>Career level:</strong></div>
                                            <div className="modal-final-step__summary-value text-right">{careerName}</div>
                                        </div>
                                        <div className="modal-final-step__summary-row">
                                            <div className="modal-final-step__summary-label text-left"><strong>Deadline:</strong></div>
                                            <div className="modal-final-step__summary-value text-right">{formatDeadlineShort(deadline)}</div>
                                        </div>
                                        <hr />
                                    </div>
                                    <div className="modal-final-step__service visible">
                                        {selectedServices.map(key => services[key] && (
                                            <div className="modal-final-step__summary-row" key={key}>
                                                <div className="modal-final-step__summary-label text-left padding-right-none"><strong>{services[key].name}</strong></div>
                                                <div className="modal-final-step__summary-value text-right"><strong>{formatPrice(services[key].price)}</strong></div>
                                            </div>
                                        ))}
                                        {selectedFeatures.map(key => features[key] && (
                                            <div className="modal-final-step__summary-row" key={key}>
                                                <div className="modal-final-step__summary-label text-left padding-right-none"><strong>{features[key].name}</strong></div>
                                                <div className="modal-final-step__summary-value text-right"><strong>{formatPrice(features[key].price)}</strong></div>
                                            </div>
                                        ))}
                                    </div>
                                    <hr />
                                    {couponApplied && (
                                        <>
                                            <div className="modal-final-step__summary-row">
                                                <div className="modal-final-step__summary-label text-left">Subtotal</div>
                                                <div className="modal-final-step__summary-value text-right">{formatPrice(subtotal)}</div>
                                            </div>
                                            <div className="modal-final-step__summary-row modal-final-step__coupon-row">
                                                <div className="modal-final-step__summary-label text-left">
                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{verticalAlign: '-2px', marginRight: 4}}>
                                                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                                                        <line x1="7" y1="7" x2="7.01" y2="7"/>
                                                    </svg>
                                                    Coupon {coupon.data.name || coupon.code}
                                                </div>
                                                <div className="modal-final-step__summary-value text-right modal-final-step__coupon-value">-{formatPrice(discount)}</div>
                                            </div>
                                        </>
                                    )}
                                    <div className="modal-final-step__total-price">
                                        <div className="modal-final-step__price text-left">
                                            <div className="modal-final-step__summary-row">
                                                <div className="modal-final-step__summary-label text-left"><strong className="label-price">Current Price</strong></div>
                                                <div className="modal-final-step__summary-value text-right"><span className="amount-price"><AnimatedNumber value={totalPrice} format={formatPrice}/></span></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-final-step__main">
                                    <div className="modal-final-step__info-row">
                                        <div className="modal-final-step__info">
                                            <p>Improve your order details to achieve <strong>total satisfaction</strong>. <span className="hidden-xs">What if we add the
                                                following features to your order? In result you get resume that will go <strong>beyond all your expectations</strong>{' '}
                                                and will totally impress any employer!</span></p>
                                        </div>
                                    </div>
                                    <div className="modal-final-step__content">
                                        <div className="modal-final-step__options">
                                                {(options.modalServices || []).filter(k => !selectedServices.includes(k)).map(key => services[key] && (
                                                    <label key={key}
                                                           className={`modal-final-step__option resume-checkbox-field ${modalServices.includes(key) ? 'checked' : ''}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={modalServices.includes(key)}
                                                            onChange={() => toggleService(key)}
                                                        />
                                                        <span className="checkbox-block">
                                                            <span className="fa fa-check-square"></span>
                                                            <span className="fa fa-square-o"></span>
                                                        </span>
                                                        <strong>{services[key].name}</strong>
                                                        {pickArt(services[key].name)}
                                                    </label>
                                                ))}
                                                {(options.modalFeature || []).filter(k => !selectedFeatures.includes(k)).map(key => features[key] && (
                                                    <label key={key}
                                                           className={`modal-final-step__option resume-checkbox-field ${modalFeatures.includes(key) ? 'checked' : ''}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={modalFeatures.includes(key)}
                                                            onChange={() => toggleFeature(key)}
                                                        />
                                                        <span className="checkbox-block">
                                                            <span className="fa fa-check-square"></span>
                                                            <span className="fa fa-square-o"></span>
                                                        </span>
                                                        <strong>{features[key].name}</strong>
                                                        {pickArt(features[key].name)}
                                                    </label>
                                                ))}
                                                <div className="add-service-price">
                                                    <div className="modal-final-step__summary-row">
                                                        <div className="modal-final-step__new-price-label text-left price-label"><strong>New Price</strong></div>
                                                        <div className="modal-final-step__new-price-value text-right price-amount"><strong><AnimatedNumber value={newTotal} format={formatPrice}/></strong></div>
                                                    </div>
                                                </div>
                                        </div>
                                        <div className="modal-final-step__illustration text-right padding-left-xs-none">
                                            <div className="modal-final-step__hero-art" aria-hidden="true">
                                                <svg viewBox="0 0 100 100" className="modal-final-step__hero-art__doc">
                                                    <defs>
                                                        <linearGradient id="resume-final-doc-grad" x1="0" y1="0" x2="1" y2="1">
                                                            <stop offset="0" stopColor="#3fb874" />
                                                            <stop offset="1" stopColor="#2f8f57" />
                                                        </linearGradient>
                                                    </defs>
                                                    <rect x="22" y="14" width="56" height="72" rx="6" fill="url(#resume-final-doc-grad)" />
                                                    <rect x="32" y="32" width="36" height="3" rx="1.5" fill="rgba(255,255,255,.85)" />
                                                    <rect x="32" y="42" width="28" height="3" rx="1.5" fill="rgba(255,255,255,.7)" />
                                                    <rect x="32" y="52" width="32" height="3" rx="1.5" fill="rgba(255,255,255,.7)" />
                                                    <rect x="32" y="62" width="22" height="3" rx="1.5" fill="rgba(255,255,255,.55)" />
                                                    <circle cx="38" cy="22" r="3" fill="rgba(255,255,255,.9)" />
                                                    <rect x="44" y="20" width="20" height="2" rx="1" fill="rgba(255,255,255,.7)" />
                                                </svg>
                                                <svg viewBox="0 0 30 30" className="modal-final-step__hero-art__star">
                                                    <path d="M15 2l3.6 8.4L27 12l-6.6 6 1.6 9-7-4.6-7 4.6 1.6-9-6.6-6 8.4-1.6z" fill="#f5a623" />
                                                </svg>
                                                {[0, 1, 2, 3, 4].map(i => (
                                                    <span key={i} className={`modal-final-step__hero-art__twinkle modal-final-step__hero-art__twinkle--i${i}`} />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                </div>

                <div className="modal-footer modal-final-step__footer">
                    <div className="link-add-service-abort text-left">
                        <a href="javascript:void(0)" onClick={onSkip}>No, thank you</a>
                    </div>
                    <div className="link-add-service-improve">
                        <button className="pull-right" onClick={() => onConfirm(modalServices, modalFeatures)}>
                            <i className="fa fa-star" aria-hidden="true"></i>IMPROVE!
                        </button>
                    </div>
                </div>

                {/* Close button rendered last so it wins paint order over
                    `.modal-header` (which is `position: relative` via the
                    Bootstrap-modal base styles) without needing a z-index. */}
                <button
                    type="button"
                    className="modal-final-step__close"
                    onClick={onClose || onSkip}
                    aria-label="Close"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>
        </div>
    </div>;
}
